import { useQuery } from '@tanstack/react-query';

import { useSiteScope } from '@/app/site/useSiteScope';
import type { RecordBody, WorkflowBody } from '@/shared/api/generated/model';
import { recordsStages } from '@/shared/api/generated/records/records';
import { describeWhen } from '@/shared/lib/dates';
import { Button } from '@/shared/ui/Button';

import { StageBadge } from './StageBadge';
import { useStageChange } from './useStageChange';
import { actionsFor, stageOf } from './stages';
import styles from './Stages.module.scss';

/** Статус записи и кнопки действий — для её роли. */
export function StageActions({
  record,
  workflow,
  onChanged,
}: {
  record: RecordBody;
  workflow: WorkflowBody;
  onChanged: (record: RecordBody) => void;
}) {
  const scope = useSiteScope();
  const { run, dialog, busy } = useStageChange(onChanged);
  const status = stageOf(workflow, record.stage);
  const writer = scope?.can('content.write') ?? false;
  const actions =
    writer && status !== undefined ? actionsFor(workflow, status.key, scope?.role) : [];

  return (
    <div className={styles['actions']}>
      <StageBadge status={status} />
      {actions.map((transition) => (
        <Button
          key={transition.to}
          size="sm"
          variant="outline"
          tone="neutral"
          disabled={busy}
          onClick={() => {
            run(record.id, transition);
          }}
        >
          {transition.label}
        </Button>
      ))}
      {dialog}
    </div>
  );
}

/** История статусов записи. */
export function StageHistory({ recordId, workflow }: { recordId: string; workflow: WorkflowBody }) {
  const history = useQuery({
    queryKey: ['record-stages', recordId],
    queryFn: async () => {
      const response = await recordsStages(recordId);

      if (response.status !== 200) {
        throw new Error('Неожиданный ответ на историю статусов');
      }

      return response.data.items ?? [];
    },
  });

  const items = history.data ?? [];

  if (items.length === 0) {
    return <p className={styles['muted']}>Статус ещё не меняли.</p>;
  }

  return (
    <ol className={styles['history']}>
      {[...items].reverse().map((item, index) => (
        <li key={`${item.created_at}-${String(index)}`} className={styles['history__item']}>
          <div className={styles['history__head']}>
            <StageBadge status={stageOf(workflow, item.to)} />
            <span className={styles['muted']}>
              {item.actor || 'кто-то из команды'} · {describeWhen(item.created_at)}
            </span>
          </div>
          {item.comment !== undefined && item.comment !== '' && (
            <p className={styles['comment']}>{item.comment}</p>
          )}
        </li>
      ))}
    </ol>
  );
}
